<template>
  <div :class="[$style.filterWrapper]">
    <!-- region Filters -->
    <div
      v-for="(filter, i) in filters"
      :key="`box-${i}${filter.id}`"
    >
      <div v-if="!disabledFilters.includes(filter.id)" :class="[$style.filterBox, {[$style.noHeader]: !filter.id}]">
        <!-- region Filter with title -->
        <LazyHydrate when-visible>
          <aw-accordion
            v-if="filter.id"
            :key="`filter-accordion-${filter.id}-${filterBoxIsActive(filter)}-${selectedFilterItem(filter)}`"
            :styling="`filter-box-${filterBoxIsActive(filter) || (Boolean(selectedFilterItem(filter)) && filter.selectionType === 'singleSelection') ? 'active' : 'inactive'}`"
            :label="$awt('aw.common.product_list.side_filter.accordion_label')"
          >
            <aw-accordion-item
              :accordion-opened="isAccordionOpened(filter)"
              @after-opened="setOpenedFilters(filter.id, true)"
              @after-closed="setOpenedFilters(filter.id, false)"
            >
              <template #headerText>
                <span :class="$style.headerTitle">{{ filter.name }}</span>
              </template>
              <template #content>
                <!-- region Range slider -->
                <template v-if="filter.selectionType === 'range'">
                  <range-filter
                    :values="activeFilters[filter.id]"
                    :range-values="filter.values"
                    :input-special-type="specialTypes[filter.id]"
                    @range-change="activateFilter($event)"
                  />
                </template>
                <!-- endregion Range slider -->

                <!-- region Radio button -->
                <template v-if="filter.selectionType === 'singleSelection'">
                  <radio-filter
                    :activate-filter="activateFilter"
                    :active-filters="activeFilters"
                    :check-box-key="checkBoxKey"
                    :filter="filter"
                  />
                </template>
                <!-- endregion Radio button -->

                <!-- region Checkbox -->
                <template v-if="filter.selectionType === 'checkbox'">
                  <checkbox-filter
                    :activate-filter="activateFilter"
                    :active-filters="activeFilters"
                    :check-box-key="checkBoxKey"
                    :filter="filter"
                  />
                </template>
                <!-- endregion Checkbox -->
              </template>
            </aw-accordion-item>
          </aw-accordion>
        </LazyHydrate>
        <!-- endregion Filter with title -->
      </div>

      <!-- region Filter box -->
      <div v-if="useInModal" :class="$style.buttonWrapper">
        <lv-button styling="small-primary-dark" stretch @click="applyFilters">
          {{ $awt('aw.common.product_list.side_filter.apply') }}
        </lv-button>
        <lv-button styling="small-secondary-dark" stretch @click="resetFilter">
          {{ $awt('aw.common.product_list.side_filter.delete_filters') }}
        </lv-button>
      </div>
      <!-- endregion Filter box -->
    </div>
  </div>
</template>

<script>
  import { defineAsyncComponent } from 'vue';
  import { mapState } from 'pinia';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index.mjs';
  import { DEFAULT_RANGE_FILTER_NUMBER_LIMIT } from '~/awPlugins/app-config';
  import { useConfigStore } from '~~/common/stores/config';
  import AwProductListFilterMixin from '~~/common/components/Page/ProductList/AwProductListFilterMixin.js';
  import AwAccordion from '~~/common/components/Common/AwAccordion/AwAccordion.vue';
  import AwAccordionItem from '~~/common/components/Common/AwAccordion/AwAccordionItem.vue';

  export default {
    name: 'AwProductListFilters',
    components: {
      CheckboxFilter: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/Filters/CheckboxFilter')),
      RadioFilter: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/Filters/RadioFilter')),
      RangeFilter: defineAsyncComponent(() => import('./Filters/RangeFilter')),
      LvButton,
      AwAccordion,
      AwAccordionItem,
    },
    mixins: [
      AwProductListFilterMixin,
    ],
    props: {
      useInModal: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['activate-filter', 'close-modal'],
    data () {
      return {
        showFilterDeleteButton: false,
        disabledFilters: ['subcategory', 'category'],
        specialTypes: {
          price: 'price_without_currency',
        },
        openedFilters: {},
      };
    },
    computed: {

      ...mapState(useConfigStore, {
        rangeFilterNumberLimit: state => state.configCommon?.persistentConfig?.product_parameter_list_range_filter_limit ?? DEFAULT_RANGE_FILTER_NUMBER_LIMIT,
      }),
      filterBoxIsActive () {
        return (filter) => {
          if (Array.isArray(this.filterBy[filter.id])) {
            if (filter.selectionType === 'range' || (filter.rangeFilterable && filter.values.length > this.rangeFilterNumberLimit)) {
              return parseInt(filter.values[0].value) !== this.filterBy[filter.id][0][0] || parseInt(filter.values[1].value) !== this.filterBy[filter.id][0][1];
            }
            return Boolean(filter.values.find(e => this.filterBy[filter.id].includes(e.value)));
          }
          return false;
        };
      },
      checkBoxKey () {
        return (filter, item) => {
          if (this.activeFilters[filter.id] && this.activeFilters[filter.id].includes(item.value)) {
            return '-checked';
          }
          return '';
        };
      },
      rangeFilterKey () {
        return (filter) => {
          if (Array.isArray(this.activeFilters[filter.id])) {
            return `${this.activeFilters[filter.id][0]}-${this.activeFilters[filter.id][1]}`;
          }
          return '';
        };
      },
      selectedFilterItem () {
        return filter => this.activeFilters[filter.id] ? this.activeFilters[filter.id].length : 0;
      },

      filterByIsActive () {
        return Boolean(Object.keys(this.filterBy).length);
      },
    },
    watch: {
      activeFilters: {
        deep: true,
        handler () {
          this.activeFiltersIsNotEmpty();
        },
      },
      filterBy (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          if (JSON.stringify(newVal) === JSON.stringify({})) {
            this.activeFilters = {};
          }
        }
      },
    },
    mounted () {
      this.initActiveFilters();
    },
    methods: {
      setOpenedFilters (filterId, value) {
        this.openedFilters[filterId] = value;
      },
      isAccordionOpened (filter) {
        const isSingleSelection = filter.selectionType === 'singleSelection';
        const isSelectedFilterItem = Boolean(this.selectedFilterItem(filter));
        if (this.useInModal) {
          return this.filterBoxIsActive(filter) || (isSelectedFilterItem && isSingleSelection);
        } else {
          return this.openedFilters[filter.id] || filter.open || (this.filterBoxIsActive(filter) || (isSelectedFilterItem && isSingleSelection));
        }
      },
      activeFiltersIsNotEmpty () {
        this.showFilterDeleteButton = this.filters.some((filter) => {
          return !this.disabledFilters.includes(filter.id) && (this.filterBoxIsActive(filter) || (this.selectedFilterItem(filter) && filter.selectionType === 'singleSelection'));
        });
      },
      applyFilters () {
        this.$emit('activate-filter');
        setTimeout(() => {
          this.$emit('close-modal');
        }, 100);
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.filterWrapper {
  @include mobile(max) {
    padding: 0 0 56px;
  }

  .buttonWrapper {
    position: absolute;
    z-index: 10;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    padding: 16px;
    background-color: $color-background-3;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .filterBox {
    margin-bottom: 8px;

    .headerTitle {
      font-weight: $font-weight-bold-v2;
      line-height: 20px;
    }

    &.noHeader {
      @include mobile(max) {
        margin-bottom: 0;
        padding: 24px 0;
      }

      @include tablet(min) {
        padding: 32px 20px;
        border: 1px solid $color-alto;
        border-radius: 8px;
        background-color: $color-seashell;
      }
    }

    .priceRangeInputs {
      margin-bottom: 32px;

      .gridItem {
        margin-right: 8px;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .filterSelectedInfo {
      margin-top: 5px;

      .label {
        font-size: 13px;
      }
    }
  }
}
</style>
